import { useState, type FormEvent } from 'react';
import api from '../../api/client';
import { SKILL_LEVELS, type SkillItem, type SkillLevel } from '../types';
import styles from '../ExpertProfileTab.module.css';

interface SkillsSectionProps {
  skills: SkillItem[];
  onUpdate: () => Promise<void>;
  apiBase?: string;
}

/** Filtert korrupte Einträge (ohne name, leere Objekte etc.) */
function sanitizeSkills(raw: unknown): SkillItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is SkillItem => {
      if (!item || typeof item !== 'object') return false;
      const s = item as Record<string, unknown>;
      return typeof s.name === 'string' && s.name.trim().length > 0;
    })
    .map((s, i) => ({
      name: s.name,
      level: (['basic', 'advanced', 'expert'].includes(s.level) ? s.level : 'advanced') as SkillLevel,
      sortOrder: typeof s.sortOrder === 'number' ? s.sortOrder : i,
    }));
}

export function SkillsSection({ skills: rawSkills, onUpdate, apiBase = '/expert-profile/me' }: SkillsSectionProps) {
  const [name, setName] = useState('');
  const [level, setLevel] = useState<SkillLevel>('advanced');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const skills = sanitizeSkills(rawSkills);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (skills.some((s) => s.name.toLowerCase() === name.trim().toLowerCase())) {
      setError('Skill bereits vorhanden');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const updated = [...skills, { name: name.trim(), level, sortOrder: skills.length }];
      // Nur Namen als Strings senden — Backend speichert String[]
      await api.patch(`${apiBase}/skills`, { skills: updated.map(s => s.name) });
      setName('');
      await onUpdate();
    } catch {
      setError('Fehler beim Hinzufügen');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (skillName: string) => {
    setLoading(true);
    setError('');
    try {
      const updated = skills
        .filter((s) => s.name !== skillName)
        .map((s, i) => ({ ...s, sortOrder: i }));
      // Nur Namen als Strings senden — Backend speichert String[]
      await api.patch(`${apiBase}/skills`, { skills: updated.map(s => s.name) });
      await onUpdate();
    } catch {
      setError('Fehler beim Löschen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Skills & Kompetenzen</h3>
        <form onSubmit={handleAdd} className={styles.headerForm}>
          <div className={styles.headerFormInputs}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Kubernetes"
              maxLength={100}
              required
            />
            <select value={level} onChange={(e) => setLevel(e.target.value as SkillLevel)}>
              {SKILL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <button type="submit" className={styles.btnPrimary} disabled={loading || !name.trim()}>
            Hinzufügen
          </button>
        </form>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {skills.length > 0 ? (
        <div className={styles.entryList}>
          {skills.map((skill) => {
            const levelInfo = SKILL_LEVELS.find((l) => l.value === skill.level);
            return (
              <div key={skill.name} className={styles.entryItem}>
                <div className={styles.entryInfo}>
                  <span className={styles.entryPrimary}>{skill.name}</span>
                  <span className={styles.entryBadge} style={{
                    background: levelInfo ? `${levelInfo.color}18` : undefined,
                    color: levelInfo?.color ?? undefined,
                  }} title={levelInfo?.label ?? skill.level}>
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      background: levelInfo?.color ?? '#94a3b8', flexShrink: 0,
                    }} />
                    {levelInfo?.label ?? skill.level}
                  </span>
                </div>
                <div className={styles.entryActions}>
                  <button
                    type="button"
                    className={`${styles.btnIcon} ${styles.btnIconDanger}`}
                    onClick={() => handleDelete(skill.name)}
                    disabled={loading}
                    title="Löschen"
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={styles.emptyState}>Noch keine Skills hinzugefügt</p>
      )}
    </div>
  );
}
